'use client';

import { useLinkStatus } from 'next/link';
import { useParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { Link } from '@/components/ui';
import { cn } from '@/lib/utils';
import type { EventTab, EventTabId } from '@/app/[locale]/event/[eventId]/libs/eventTabs';
import { useWarmTabChunks } from './useWarmTabChunks';

interface EventNavigationProps {
  tabs: EventTab[];
  activeTab: EventTabId;
  className?: string;
}

/**
 * Lives inside a tab's <Link>: while that tab's navigation is in flight (prefetch is off, so
 * every tab click waits on the server) it sets `data-tab-pending`. The tab then reads as active
 * with a pulsing underline, and the shell dims its content from the same marker. Always
 * rendered, so turning it on never shifts the layout.
 */
function TabPendingHint() {
  const { pending } = useLinkStatus();
  return (
    <span
      aria-hidden
      data-tab-hint
      data-tab-pending={pending ? '' : undefined}
      className="bg-primary pointer-events-none absolute inset-x-0 bottom-0 h-0.5 opacity-0 data-tab-pending:opacity-100 motion-safe:data-tab-pending:animate-pulse"
    />
  );
}

export function EventNavigation({ tabs, activeTab, className }: EventNavigationProps) {
  const t = useTranslations('Event.navigation');
  const params = useParams();
  const baseUrl = `/${params.locale as string}/event/${params.eventId as string}/${params.slug as string}`;
  // Tab links are not prefetched (that would call the server per tab); their JS is warmed instead.
  useWarmTabChunks(tabs.map((tab) => tab.path));

  return (
    <div data-event-nav className={cn('bg-card border-border border-b', className)}>
      <nav className="flex overflow-x-auto [mask-image:linear-gradient(90deg,black_calc(100%-28px),transparent)] pe-7 whitespace-nowrap [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
        {tabs.map((tab) => {
          const active = tab.id === activeTab;
          return (
            <Link
              key={tab.id}
              href={`${baseUrl}${tab.path}`}
              aria-current={active ? 'page' : undefined}
              className={cn(
                // relative: the sr-only live label is absolutely positioned and must stay inside the scroller
                'text-md relative flex min-h-11 items-center gap-1.5 px-4 py-2 font-medium transition-colors',
                active
                  ? 'text-primary border-primary border-b-2'
                  : 'text-foreground has-[[data-tab-pending]]:text-primary',
              )}
            >
              <TabPendingHint />
              {tab.live && (
                <span data-live-dot className="relative flex h-2 w-2 shrink-0" aria-hidden>
                  <span className="bg-danger absolute inline-flex h-full w-full animate-ping rounded-full opacity-75 motion-reduce:animate-none" />
                  <span className="bg-danger relative inline-flex h-2 w-2 rounded-full" />
                </span>
              )}
              <span>
                {t(tab.labelKey ?? tab.id)}
                {tab.live && (
                  <>
                    {' '}
                    <span className="sr-only">{t('liveSchedule')}</span>
                  </>
                )}
              </span>
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
